import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useQuery } from "./AppLayout";
import { getJirItemUrl } from "./api/api";
import { navigateTo } from "./Navigator";
import { ChevronLeft } from "lucide-react";
import { workspaceNavSetting } from "./AppNav";
import classNames from "classnames";

const AppNavSettingsItem = ({ item, isGroup, currentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const query: any = useQuery();

  if (item.separator) {
    return (
      <li key={item.name} title={item.name}>
        <div className="relative py-1">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-2 text-sm text-gray-500"></span>
          </div>
        </div>
      </li>
    );
  }

  const currentItem = query.get("item");
  const isCurrent =
    location.pathname?.includes(item.suffix) &&
    (!currentItem || item.search?.includes(`?item=${currentItem}`));

  const menuItem = (
    <>
      {item.icon ? (
        <item.icon
          className={classNames(
            isCurrent
              ? "bg-gray-50 text-blue-600"
              : "text-gray-700 group-hover:text-blue-600 group-hover:bg-gray-50",
            "h-5 w-5 shrink-0"
          )}
          size={20}
          aria-hidden="true"
        />
      ) : null}
      <span className="font-medium">{item.name}</span>
    </>
  );

  if (item.children?.length > 0) {
    return (
      <>
        <li title={item.name} className="mt-3">
          <div title={item.name} className="text-gray-700 p-1">
            <span className="text-xs font-medium text-gray-600">{item.name}</span>
          </div>
        </li>
        {item.children.map((itemChild) => (
          <AppNavSettingsItem
            key={itemChild.name}
            item={itemChild}
            isGroup={true}
            currentUser={currentUser}
          />
        ))}
      </>
    );
  }

  const navItemUrl = item.href == "" ? "/workspaces" : `${item.href}/${item.suffix}${item.search}`;

  return (
    <li key={item.name} title={item.name} className={isGroup ? "pl-4" : ""}>
      <Link
        to={navItemUrl}
        onClick={() => {
          navigateTo({
            jiraUrlParams: {
              relativeUrl: item.jiraUrl,
            },
            appUrl: navItemUrl,
            navigate,
            forceUseNavigate: true,
          });
        }}
        title={item.name}
        className={classNames(
          isCurrent
            ? "bg-gray-100 text-blue-600"
            : "text-gray-700 hover:text-blue-600 hover:bg-gray-100",
          "group flex items-center gap-x-3 rounded-md p-1 cursor-pointer"
        )}
      >
        {menuItem}
      </Link>
    </li>
  );
};

const AppNavSettings = () => {
  const { t } = useTranslation();
  const { projectId }: any = useParams();
  const navigate = useNavigate();

  const tenantInfo = useSelector((state: any) => state.tenant.get("tenantInfo"));
  const isJira = tenantInfo.getIn(["host", "product"]) === "JIRA";
  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));

  const navSettingItems = projectId ? workspaceNavSetting(projectId, isJira) : [];
  const hasProject = projectId && projectId != "";

  const renderWorkspaceNavSettingItems = () => {
    if (hasProject) {
      return (
        <li className="w-full">
          <ul role="list" className="space-y-1 p-1.5">
            {navSettingItems.map((item) => (
              <AppNavSettingsItem
                key={item.name}
                item={item}
                isGroup={false}
                currentUser={currentUser}
              />
            ))}
          </ul>
        </li>
      );
    }
  };

  return (
    <div className="w-[240px] flex-shrink-0 border-r border-gray-200">
      <div className="w-full h-12 px-2 flex items-center gap-4 border-b border-gray-200">
        <div
          className="p-2 rounded-md hover:bg-gray-100 cursor-pointer"
          onClick={() => {
            const appUrl = `/workspaces/${projectId}?view=BOARD&type=SALE&key=Deals`;
            navigateTo({
              jiraUrlParams: {
                relativeUrl: getJirItemUrl({ workspaceId: projectId, key: "Deals" }),
              },
              appUrl,
              navigate,
              forceUseNavigate: true,
            });
          }}
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </div>
        <div className="font-semibold">{t("Settings")}</div>
      </div>
      <nav className="w-full h-[calc(100dvh-48px)] flex flex-col">
        <ul role="list" className="flex items-center flex-1 flex-col gap-y-1.5">
          {renderWorkspaceNavSettingItems()}
        </ul>
      </nav>
    </div>
  );
};

export default AppNavSettings;
